"use client";

/**
 * Impersonation engine — stale-route guard.
 *
 * Impersonation builds up browser history INSIDE a client-experience route tree
 * (/client, /participant, /family, /corporate-admin). After the user exits, those
 * history entries remain — pressing browser Back (or refreshing / deep-linking) lands
 * on a page that only works under impersonation, now with NO impersonation context
 * (broken data, wrong identity).
 *
 * This guard catches that: when we are NOT impersonating, yet a STAFF user (reviewer /
 * advisor / firm-admin / enterprise-admin — roles that only ever reach these trees BY
 * impersonating) is sitting on one of those routes, send them back to where they launched
 * the session from (`readEntryOrigin()`), falling back to `/landing` (which re-routes by
 * their real role) when no origin was recorded.
 *
 * Keyed on impersonation STATE, not role — because the role lags during a context
 * switch. During ENTRY `state` is non-null, so this never fires (no false redirect mid
 * switch even while the role is still flipping); only once `state` is null (exited /
 * never impersonating) does the guard apply. Real client-side users (HOH / family
 * member / self-directed / corporate-admin) are never "staff", so they're never bounced.
 *
 * Returns whether the current route is a stale one we're bouncing away from, so the
 * provider can render the context loader INSTEAD of the (broken) page while the redirect
 * happens — otherwise the page paints for a frame on browser Back before the effect runs.
 * Gated by `enabled` (hydration complete): until sessionStorage state is read, `state` is
 * transiently null even on a legitimately-impersonated page, so we must not redirect yet.
 */
import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useRoleDeterminer } from "@/shared/hooks/useRoleDeterminator";
import { readEntryOrigin } from "./core";
import type { ImpersonationState } from "./types";

/** Route trees a staff user can only be inside via impersonation. */
const CLIENT_EXPERIENCE_PREFIXES = [
  "/client/",
  "/participant/",
  "/family/",
  "/corporate-admin/",
];

interface StaleRouteGuardArgs {
  state: ImpersonationState;
  pathname: string;
  /** Hydration complete — until then `state` is transiently null; don't redirect. */
  enabled: boolean;
}

export function useStaleImpersonationRouteGuard({
  state,
  pathname,
  enabled,
}: StaleRouteGuardArgs): boolean {
  const router = useRouter();
  // Reuse the canonical role logic (case-insensitive, handles the "Financial Advisor"
  // literal) instead of matching role strings by hand.
  const { isReviewer, isFinancialAdvisor, isFirmAdmin, isEnterpriseAdmin } =
    useRoleDeterminer();
  const isStaff =
    isReviewer || isFinancialAdvisor || isFirmAdmin || isEnterpriseAdmin;

  const onClientExperienceRoute = CLIENT_EXPERIENCE_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix),
  );
  // Not impersonating (`!state`) yet a staff user sitting on a client-experience route
  // (reachable only via impersonation) → stale. Block the paint + redirect home.
  const blocked = enabled && !state && onClientExperienceRoute && isStaff;

  useEffect(() => {
    // Back to where this impersonation session was launched from (survives the exit that
    // cleared the stack); `/landing` as a fallback when no origin was recorded.
    if (blocked) router.replace(readEntryOrigin() ?? "/landing");
  }, [blocked, router]);

  return blocked;
}
